/* eslint-disable */
import React, { useEffect, useId, useRef } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled, { keyframes } from "styled-components";
import { Icon } from "../Icon/Icon";
import { createPortal } from "react-dom";

/* ─── Variant themes ─── */

const VARIANT_THEMES = {
	default: {
		bg: "#f3f5ff",
		shadow: "0px 12px 24px 0px #1559a53d",
		primaryBg: "#3a0ca3",
		primaryHover: "#3a0ca3",
		outlineBorder: "#3a0ca3",
		outlineColor: "#3a0ca3",
	},
	danger: {
		bg: "#ffebef",
		shadow: "0px 12px 24px 0px #d800273d",
		primaryBg: "#D30024",
		primaryHover: "#D30024",
		outlineBorder: "#2e1a89",
		outlineColor: "#2e1a89",
	},
} as const;

type Variant = keyof typeof VARIANT_THEMES;

/* ─── Styled components ─── */

const overlayFade = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const dialogPop = keyframes`
	from { opacity: 0; transform: translateY(-8px) scale(0.98); }
	to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2200;
	display: flex;
	align-items: center;
	justify-content: center;
	animation: ${overlayFade} 160ms ease forwards;
`;

const Mask = styled.div`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	z-index: 2200;
`;

const Dialog = styled.div<{ $width: number; $variant: Variant; $maxHeight?: string }>`
	position: relative;
	z-index: 2201;
	width: ${({ $width }) => $width}px;
	max-width: calc(100% - 48px);
	max-height: ${({ $maxHeight }) => $maxHeight || "calc(100vh - 48px)"};
	border-radius: 24px;
	background: ${({ $variant }) => VARIANT_THEMES[$variant].bg};
	box-shadow: ${({ $variant }) => VARIANT_THEMES[$variant].shadow};
	outline: none;
	transform-origin: center center;
	animation: ${dialogPop} 180ms cubic-bezier(0.2, 0, 0, 1) forwards;

	/* Remove default focus outline / ring for the dialog itself so
		 focusing the modal (for keyboard handling) doesn't add a visible border. */
	&:focus,
	&:focus-visible {
		outline: none;
		box-shadow: ${({ $variant }) => VARIANT_THEMES[$variant].shadow};
	}
`;

const ModalContent = styled.div<{ $scrollable?: boolean; $maxHeight?: string }>`
	position: relative;
	padding: 32px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
	max-height: ${({ $maxHeight }) => $maxHeight || "none"};
	overflow-y: ${({ $scrollable }) => ($scrollable ? "auto" : "visible")};
`;

const CloseBtn = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #666;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;

	&:hover:not(:disabled) {
		background: rgba(0, 0, 0, 0.06);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

const HeaderContainer = styled.div<{ $centered?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 24px;
	width: 100%;
	align-items: ${({ $centered }) => ($centered ? "center" : "flex-start")};
`;

const Title = styled.div<{ $centered?: boolean }>`
	font-size: 24px;
	font-weight: 600;
	color: #000;
	line-height: 1.2;
	text-align: ${({ $centered }) => ($centered ? "center" : "left")};
`;

const Description = styled.div<{ $centered?: boolean }>`
	font-size: 15px;
	color: #555;
	line-height: 1.55;
	margin-top: -16px;
	margin-bottom: 16px;
	width: 100%;
	text-align: ${({ $centered }) => ($centered ? "center" : "left")};
`;

const ChildrenWrapper = styled.div`
	width: 100%;
	margin-bottom: 24px;

	/* ─── Default input styles for Ant Design form controls ─── */

	.ant-form-item-label > label {
		color: #707070 !important;
		font-weight: 400 !important;
		height: auto !important;
		line-height: 1.2 !important;
		padding-bottom: 4px !important;
		font-size: 14px !important;
	}

	.ant-form-item {
		margin-bottom: 16px !important;
	}

	.ant-form-item-label {
		padding: 0 !important;
	}

	/* Shared base for all input-like controls */
	.ant-input,
	.ant-input-number,
	.ant-input-affix-wrapper,
	.ant-picker,
	.ant-select-selector {
		border: 1px solid #cdd4f6 !important;
		border-radius: 8px !important;
		padding: 0 12px !important;
		height: 44px !important;
		display: flex !important;
		align-items: center !important;
		font-size: 15px !important;
		background: #fff !important;
		transition:
			border-color 0.2s,
			box-shadow 0.2s;
		width: 100% !important;
	}

	.ant-select {
		width: 100% !important;
		margin-bottom: 12px !important;
	}

	/* InputNumber inner input alignment */
	.ant-input-number-input {
		height: 42px !important;
		font-size: 15px !important;
		padding: 0 !important;
	}

	/* Select specific fixes */
	.ant-select .ant-select-selector {
		padding-right: 40px !important;
	}

	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		padding: 0 !important;
		line-height: 44px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-select-single .ant-select-arrow {
		inset-inline-end: 12px !important;
		top: 0 !important;
		transform: none !important;
		height: 44px !important;
		width: 16px !important;
		margin: 0 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		line-height: 1 !important;
	}

	.ant-select-selection-search {
		display: flex !important;
		align-items: center !important;
	}

	/* TextArea — auto height, keep border-radius */
	.ant-input[type="textarea"],
	textarea.ant-input {
		height: auto !important;
		min-height: 80px;
		padding: 12px !important;
	}

	/* Focus / hover ring */
	.ant-input:focus,
	.ant-input-focused,
	.ant-input:hover,
	.ant-input-number:hover,
	.ant-input-number-focused,
	.ant-input-affix-wrapper:hover,
	.ant-input-affix-wrapper-focused,
	.ant-picker:hover,
	.ant-picker-focused,
	.ant-select:not(.ant-select-disabled):hover .ant-select-selector,
	.ant-select-focused .ant-select-selector {
		border-color: #3a0ca3 !important;
		box-shadow: 0 0 0 2px rgba(58, 12, 163, 0.1) !important;
	}

	/* Placeholder color */
	.ant-input::placeholder,
	.ant-input-number-input::placeholder,
	.ant-select-selection-placeholder {
		color: #aaa !important;
	}

	/* Remove bottom margin from last form item to avoid double spacing before buttons */
	.ant-form-item:last-child {
		margin-bottom: 0 !important;
	}
`;

const ButtonGroup = styled.div<{ $single?: boolean; $fullWidth?: boolean; $compact?: boolean }>`
	display: flex;
	justify-content: ${({ $compact }) => ($compact ? "center" : "space-between")};
	gap: 16px;
	margin-left: ${({ $compact }) => ($compact ? "0" : "auto")};
	align-items: center;
	width: 100%;
	max-width: ${({ $single, $fullWidth, $compact }) =>
		$compact ? "364px" : $single || $fullWidth ? "none" : "520px"};
	font-size: 16px;
	align-self: ${({ $compact }) => ($compact ? "center" : "stretch")};
`;

const OutlineButton = styled(Button)<{ $variant: Variant; $actionHeight: number }>`
	flex: 1;
	height: ${({ $actionHeight }) => $actionHeight}px;
	border-radius: 24px;
	font-size: 16px;
	font-weight: 600;
	border: 2px solid ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
	color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineColor} !important;
	background: #fff;
	transition: all 0.2s !important;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
		border-color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineBorder} !important;
		color: ${({ $variant }) => VARIANT_THEMES[$variant].outlineColor} !important;
	}

	&:disabled,
	&.ant-btn-disabled {
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		background: #f5f5f5 !important;
		cursor: not-allowed;
		opacity: 1 !important;
		box-shadow: none !important;
	}
`;

const PrimaryButton = styled(Button)<{ $variant: Variant; $actionHeight: number; $pill?: boolean }>`
	flex: 1;
	height: ${({ $actionHeight }) => $actionHeight}px;
	border-radius: ${({ $pill }) => ($pill ? "64px" : "24px")};
	font-weight: 600;
	font-size: 16px;
	background: ${({ $variant }) => VARIANT_THEMES[$variant].primaryBg} !important;
	border: none;
	color: #fff !important;
	text-decoration: none !important;

	&:hover {
		background: ${({ $variant }) => VARIANT_THEMES[$variant].primaryHover} !important;
		opacity: 0.9;
	}

	&:disabled,
	&.ant-btn-disabled {
		background: #d9d9d9 !important;
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		cursor: not-allowed;
		opacity: 1 !important;
	}
`;

/* ─── Public interface ─── */

export interface DynamicModalProps {
	open: boolean;
	onCancel: () => void;
	title: React.ReactNode;
	description?: React.ReactNode;
	icon?: { src: string; color?: string; size?: number } | React.ReactNode;
	/** Body slot — renders between description & buttons. Use for forms, inputs, etc. */
	children?: React.ReactNode;
	variant?: Variant;
	/** Primary action button label (default: "Confirm") */
	confirmText?: string;
	onConfirm: () => void;
	confirmDisabled?: boolean;
	loading?: boolean;
	/** Secondary action button label (default: "Cancel") */
	cancelText?: string;
	width?: number;
	/** Center heading, description and icon; used by the creator welcome experience. */
	centered?: boolean;
	/** Use a single outlined confirmation control instead of the standard button pair. */
	singleOutlineAction?: boolean;
	/** Use a single filled confirmation control instead of the standard button pair. */
	singlePrimaryAction?: boolean;
	/** Render the primary control as a native link, e.g. mailto:. `onConfirm` still runs on click. */
	confirmHref?: string;
	/** Prevent dismissal until the required action completes. */
	closable?: boolean;
	/** Render the top-right close control independently of Escape/mask dismissal. */
	showCloseButton?: boolean;
	/** Override the shared 48px action height for a specific design contract. */
	actionHeight?: number;
	/** Let paired action buttons span the full modal content width. */
	fullWidthActions?: boolean;
	/** Allow tall modal content to scroll within the viewport. */
	scrollable?: boolean;
	maxHeight?: string;
}

export const DynamicModal: React.FC<DynamicModalProps> = ({
	open,
	onCancel,
	title,
	description,
	icon,
	children,
	variant = "default",
	confirmText = "Confirm",
	onConfirm,
	confirmDisabled = false,
	loading = false,
	cancelText = "Cancel",
	width = 432,
	centered = false,
	singleOutlineAction = false,
	singlePrimaryAction = false,
	confirmHref,
	closable = true,
	showCloseButton = true,
	actionHeight = 48,
	fullWidthActions = false,
	scrollable = false,
	maxHeight,
}) => {
	const keyboard = !loading && closable;
	const maskClosable = !loading && closable;
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const prevActiveRef = useRef<HTMLElement | null>(null);
	const titleId = useId();
	const descriptionId = useId();

	// Stable ref for onCancel to avoid re-running the effect when the callback identity changes.
	// This prevents focus-stealing when a parent re-renders (e.g. during typing in child inputs).
	const onCancelRef = useRef(onCancel);
	onCancelRef.current = onCancel;

	useEffect(() => {
		if (!open) return;
		prevActiveRef.current = document.activeElement as HTMLElement | null;
		// lock body scroll
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		// focus the dialog for keyboard handling
		setTimeout(() => dialogRef.current?.focus(), 0);

		const focusableSelector =
			'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable=true]';

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" && keyboard) {
				e.stopPropagation();
				onCancelRef.current();
				return;
			}
			if (e.key === "Tab") {
				const root = dialogRef.current;
				if (!root) return;
				const nodes = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
					(el) =>
						(el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0) &&
						!el.hasAttribute("disabled"),
				);
				if (nodes.length === 0) {
					e.preventDefault();
					return;
				}
				const first = nodes[0];
				const last = nodes[nodes.length - 1];
				const active = document.activeElement as HTMLElement | null;
				if (e.shiftKey) {
					if (active === first || active === root) {
						e.preventDefault();
						last.focus();
					}
				} else {
					if (active === last) {
						e.preventDefault();
						first.focus();
					}
				}
			}
		};

		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			document.removeEventListener("keydown", onKeyDown, true);
			// restore scroll
			document.body.style.overflow = prevOverflow;
			// restore focus
			try {
				prevActiveRef.current?.focus();
			} catch (e) {
				/* ignore */
			}
		};
	}, [open, keyboard]);

	if (!open) return null;

	return createPortal(
		<Overlay>
			<Mask onClick={() => maskClosable && !loading && onCancel()} />
			<Dialog
				ref={dialogRef}
				role="dialog"
				aria-modal
				aria-labelledby={titleId}
				aria-describedby={description ? descriptionId : undefined}
				tabIndex={-1}
				$width={width}
				$variant={variant}
				$maxHeight={maxHeight}
				onClick={(e) => e.stopPropagation()}
			>
				<ModalContent $scrollable={scrollable} $maxHeight={maxHeight}>
					{showCloseButton && closable && (
						<CloseBtn aria-label="Close" onClick={onCancel} disabled={loading}>
							<CloseOutlined />
						</CloseBtn>
					)}

					<HeaderContainer $centered={centered}>
						{icon && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: centered ? "center" : undefined,
								}}
							>
								{typeof icon === "object" && icon !== null && "src" in icon ? (
									<Icon src={icon.src} size={icon.size ?? 32} color={icon.color} />
								) : (
									icon
								)}
							</div>
						)}
						<Title id={titleId} $centered={centered}>
							{title}
						</Title>
					</HeaderContainer>

					{description && (
						<Description id={descriptionId} $centered={centered}>
							{description}
						</Description>
					)}

					{children && <ChildrenWrapper>{children}</ChildrenWrapper>}

					<ButtonGroup
						$single={singleOutlineAction || singlePrimaryAction}
						$fullWidth={fullWidthActions}
						$compact={singlePrimaryAction}
					>
						{singleOutlineAction ? (
							<OutlineButton
								$variant={variant}
								$actionHeight={actionHeight}
								onClick={onConfirm}
								disabled={confirmDisabled || loading}
								loading={loading}
							>
								{confirmText}
							</OutlineButton>
						) : singlePrimaryAction ? (
							<PrimaryButton
								$variant={variant}
								$actionHeight={actionHeight}
								$pill
								type="primary"
								href={confirmHref}
								target={confirmHref ? "_blank" : undefined}
								rel={confirmHref ? "noopener noreferrer" : undefined}
								onClick={onConfirm}
								disabled={confirmDisabled || loading}
								loading={loading}
							>
								{confirmText}
							</PrimaryButton>
						) : (
							<>
								<OutlineButton
									$variant={variant}
									$actionHeight={actionHeight}
									onClick={onCancel}
									disabled={loading}
								>
									{cancelText}
								</OutlineButton>
								<PrimaryButton
									$variant={variant}
									$actionHeight={actionHeight}
									type="primary"
									href={confirmHref}
									target={confirmHref ? "_blank" : undefined}
									rel={confirmHref ? "noopener noreferrer" : undefined}
									onClick={onConfirm}
									disabled={confirmDisabled || loading}
									loading={loading}
								>
									{confirmText}
								</PrimaryButton>
							</>
						)}
					</ButtonGroup>
				</ModalContent>
			</Dialog>
		</Overlay>,
		document.body,
	);
};
